import TicketIcon from "../../../../../assets/svg/ticket-icon";
import VerbacallIcon from "../../../../../assets/svg/verbacall-icon";
import VerbachatIcon from "../../../../../assets/svg/verbachat-icon";
import VerbamailIcon from "../../../../../assets/svg/verbamail-icon";
import VerbamessengerIcon from "../../../../../assets/svg/verbamessenger-icon";
import VerbamessengerColouredIcon from "../../../../../assets/svg/verbamessenger-coloured-icon";
import VerbasmsIcon from "../../../../../assets/svg/verbasms-icon";
import FbMessengerIcon from "../../../../../assets/svg/fb-messenger-icon";
import InstagramIcon from "../../../../../assets/svg/instagram-icon";
import { ConvertedLeadFormData } from "../../../../../utils/functions/data-converter-functions";
import { LeadDetails, ModalityConfig } from "../../../utils/types";
import {
  getDefaultBudgetDuration,
  getDefaultCurrencyUnit,
  getLeaseDurationUnit,
} from "../../../../../utils/constants/mappings";
import { isEmptyPhoneNumber } from "../../../../../components/PhoneNumberCountryCodeSelector/utils/phoneNumberUtils";
import MentionsIcon from "../../../../../assets/svg/mentions-icon";
import { ExtendedLeadFormValues, FormFieldsConfig } from "../utils/types";

type PayloadType = Omit<
  Partial<ConvertedLeadFormData>,
  | "phone_country_code"
  | "phone"
  | "whatsapp_phone_country_code"
  | "whatsapp_phone"
> & {
  min_budget?: number | null;
  max_budget?: number | null;
  utm_source?: string | null;
  utm_medium?: string | null;
  choosen_props?: Array<{ property_id: number; property_name: string }>;
  phone_country_code?: string | null;
  phone?: string | null;
  whatsapp_phone_country_code?: string | null;
  whatsapp_phone?: string | null;
  room_type?: string[];
  budget?: {
    min_budget: number;
    max_budget: number;
    currency: string;
    duration: string;
    display_value: string;
  };
  demographics?: {
    destination_country_id?: string;
    destination_country?: string;
    destination_city_id?: string;
    destination_city?: string;
    university_id?: string;
    university?: string;
  };
  current_stage?: string;
  how_did_you_hear_about_us?: string;
  marketing_consent?: boolean;
  agent_id?: string[] | null;
};

export const transformFormValuesToApiPayload = (
  values: ExtendedLeadFormValues,
  prevLeadDetails: LeadDetails | null,
  region?: string
) => {
  const payload: PayloadType = {};

  if (values.name !== prevLeadDetails?.name) payload.name = values.name;
  if (values.email !== prevLeadDetails?.email) payload.email = values.email;

  const currPhoneEmpty = isEmptyPhoneNumber(values?.phoneNumber);
  const prevPhoneEmpty = isEmptyPhoneNumber(prevLeadDetails?.phone);
  const phoneChanged = currPhoneEmpty !== prevPhoneEmpty ||
    (!currPhoneEmpty && !prevPhoneEmpty && (
      values?.phoneNumber?.trim() !== prevLeadDetails?.phone?.trim() ||
      values?.phoneCountry?.dialCode !== prevLeadDetails?.phone_country_code
    ));
  if (phoneChanged) {
    if (currPhoneEmpty) {
      payload.phone_country_code = null;
      payload.phone = null;
    } else {
      payload.phone_country_code = values.phoneCountry?.dialCode;
      payload.phone = values.phoneNumber;
    }
  }

  const currWhatsappEmpty = isEmptyPhoneNumber(values?.whatsappNumber);
  const prevWhatsappEmpty = isEmptyPhoneNumber(prevLeadDetails?.whatsapp_phone);
  const whatsappChanged = currWhatsappEmpty !== prevWhatsappEmpty ||
    (!currWhatsappEmpty && !prevWhatsappEmpty && (
      values?.whatsappNumber?.trim() !== prevLeadDetails?.whatsapp_phone?.trim() ||
      values?.whatsappCountry?.dialCode !== prevLeadDetails?.whatsapp_phone_country_code
    ));
  if (whatsappChanged) {
    if (currWhatsappEmpty) {
      payload.whatsapp_phone_country_code = null;
      payload.whatsapp_phone = null;
    } else {
      payload.whatsapp_phone = values.whatsappNumber;
      payload.whatsapp_phone_country_code = values.whatsappCountry?.dialCode;
    }
  }

  if (
    (values.destinationCountry || "") !==
    (prevLeadDetails?.demographics?.destination_country_id?.toString() || "")
  ) {
    payload.demographics = {
      ...(payload.demographics || {}),
      destination_country_id: values.destinationCountry ?? undefined,
      destination_country: values.destinationCountryName ?? undefined,
    };
  }
  if (
    (values.destinationCity || "") !==
    (prevLeadDetails?.demographics?.destination_city_id?.toString() || "")
  ) {
    payload.demographics = {
      ...(payload.demographics || {}),
      destination_city_id: values.destinationCity ?? undefined,
      destination_city: values.destinationCityName ?? undefined,
    };
  }
  if (
    (values.university || "") !==
    (prevLeadDetails?.demographics?.university_id?.toString() || "")
  ) {
    payload.demographics = {
      ...(payload.demographics || {}),
      university_id: values.university ?? undefined,
      university: values.universityName ?? undefined,
    };
  }

  if (payload.demographics && Object.keys(payload.demographics).length === 0) {
    delete payload.demographics;
  }

  const budgetChanged =
    values.min_budget !== prevLeadDetails?.budget?.min_budget?.toString() ||
    values.max_budget !== prevLeadDetails?.budget?.max_budget?.toString();

  if (budgetChanged) {
    const minBudget = values.min_budget ? parseInt(values.min_budget) : 0;
    const maxBudget = values.max_budget ? parseInt(values.max_budget) : 0;
    const currency =
      prevLeadDetails?.budget?.currency || getDefaultCurrencyUnit(region);
    const duration =
      prevLeadDetails?.budget?.duration || getDefaultBudgetDuration(region);

    const currencySymbol =
      currency === "GBP" ? "£" : currency === "USD" ? "$" : "€";
    const display_value =
      minBudget && maxBudget
        ? `${currencySymbol}${minBudget}-${currencySymbol}${maxBudget}`
        : minBudget
          ? `${currencySymbol}${minBudget}+`
          : maxBudget
            ? `Up to ${currencySymbol}${maxBudget}`
            : "";

    payload.budget = {
      min_budget: minBudget,
      max_budget: maxBudget,
      currency: currency || "",
      duration: duration || "",
      display_value: display_value,
    };
  }

  if (values.stay_duration !== prevLeadDetails?.lease?.value?.toString()) {
    payload.lease = {
      value: values.stay_duration ? parseInt(values.stay_duration) : 0,
      unit: prevLeadDetails?.lease?.unit || getLeaseDurationUnit(region) || "",
    };
  }

  const prevRoomTypes = prevLeadDetails?.room_type || [];
  const currentRoomTypes = values.roomType || [];

  const sortedPrevRoomTypes = [...prevRoomTypes].sort();
  const sortedCurrentRoomTypes = [...currentRoomTypes].sort();
  if (
    JSON.stringify(sortedPrevRoomTypes) !==
    JSON.stringify(sortedCurrentRoomTypes)
  ) {
    payload.room_type = currentRoomTypes;
  }
  if (values.tenantType !== prevLeadDetails?.tenant_type) {
    payload.tenant_type = values.tenantType ?? undefined;
  }
  if (values.levelOfStudy !== prevLeadDetails?.level_of_study) {
    payload.level_of_study = values.levelOfStudy ?? undefined;
  }
  const utmSourceChanged =
    values.utmSource !== prevLeadDetails?.tracking_info?.utm_source;
  const utmMediumChanged =
    values.utmMedium !== prevLeadDetails?.tracking_info?.utm_medium;
  if (utmSourceChanged || utmMediumChanged) {
    payload.utm_source = values.utmSource ?? undefined;
    payload.utm_medium = values.utmMedium ?? undefined;
  }

  const prevChoosenPropIds = (prevLeadDetails?.choosen_props || prevLeadDetails?.chosen_props || []).map((p) => p.property_id.toString()).sort();
  const currentChoosenPropIds = (values.choosenProps || []).map(String).sort();
  if (JSON.stringify(currentChoosenPropIds) !== JSON.stringify(prevChoosenPropIds)) {
    payload.choosen_props = values.choosenPropsPayload || [];
  }

  const currentMoveInDate = values.move_in_date
    ? values.move_in_date.toISOString().split("T")[0]
    : null;
  const existingMoveInDate = prevLeadDetails?.move_in_date
    ? new Date(prevLeadDetails.move_in_date).toISOString().split("T")[0]
    : null;
  if (currentMoveInDate !== existingMoveInDate) {
    payload.move_in_date = values.move_in_date
      ? values.move_in_date.toISOString()
      : null;
  }

  const currentTagCodes = values.selectedTagOptions || [];
  const existingTagCodes =
    prevLeadDetails?.tags?.map((tag) => tag.code || tag.value) || [];
  if (
    JSON.stringify(currentTagCodes.sort()) !==
    JSON.stringify(existingTagCodes.sort())
  ) {
    payload.tags =
      values.selectedTagsData ||
      currentTagCodes.map((code: string) => ({
        name: "",
        label: "",
        code: code,
      }));
  }

  if (values.currentStage && values.currentStage !== prevLeadDetails?.current_stage) {
    payload.current_stage = values.currentStage;
  }

  if (
    (values.howDidYouHearAboutUs || "") !==
    (prevLeadDetails?.how_did_you_hear_about_us || "")
  ) {
    payload.how_did_you_hear_about_us = values.howDidYouHearAboutUs || undefined;
  }

  const prevConsent = prevLeadDetails?.marketing_consent != null
    ? (prevLeadDetails.marketing_consent ? "Yes" : "No")
    : null;
  if ((values.marketingConsent || null) !== prevConsent) {
    payload.marketing_consent = values.marketingConsent
      ? values.marketingConsent === "Yes"
      : undefined;
  }

  const prevAgentIds: string[] = Array.isArray(prevLeadDetails?.agent)
    ? prevLeadDetails.agent.map((a) => a.id)
    : [];
  const currentAgentIds = values.assigned_agents || [];
  if (JSON.stringify([...currentAgentIds].sort()) !== JSON.stringify([...prevAgentIds].sort())) {
    payload.agent_id = currentAgentIds.length > 0 ? currentAgentIds : null;
  }

  return payload;
};

export const MODALITY_CONFIG: ModalityConfig[] = [
  {
    key: "widget",
    label: "VerbaChat",
    icon: <VerbachatIcon className="w-[15px] h-[15px]" />,
    colorClass: "text-verba-chat dark:text-verba-chat-dk",
  },
  {
    key: "whatsapp",
    label: "VerbaMessenger",
    icon: <VerbamessengerIcon className="w-[15px] h-[15px]" />,
    colorClass: "text-verba-messenger dark:text-verba-messenger-dk",
  },
  {
    key: "email",
    label: "VerbaMail",
    icon: <VerbamailIcon className="w-[15px] h-[15px]" />,
    colorClass: "text-verba-mail dark:text-verba-mail-dk",
  },
  {
    key: "call",
    label: "VerbaCall",
    icon: <VerbacallIcon className="w-[15px] h-[15px]" />,
    colorClass: "text-verba-lavender dark:text-verba-lavender-dk",
  },
  {
    key: "sms",
    label: "VerbaSMS",
    icon: <VerbasmsIcon className="w-[15px] h-[15px]" />,
    colorClass: "text-verba-sms dark:text-verba-sms-dk",
  },
  {
    key: "fb_messenger",
    label: "Facebook Messenger",
    icon: <FbMessengerIcon className="w-[15px] h-[15px]" />,
    colorClass: "text-verba-fb-messenger dark:text-verba-fb-messenger-dk",
  },
  {
    key: "instagram",
    label: "Instagram DMs",
    icon: <InstagramIcon className="w-[15px] h-[15px]" />,
    colorClass: "",
  },
];

export const TOGGLE_MODALITY_CONFIG: ModalityConfig[] = [
  {
    key: "widget",
    label: "VerbaChat",
    icon: <VerbachatIcon className="w-[15px] h-[15px]" />,
    colorClass: "text-verba-chat dark:text-verba-chat-dk",
  },
  {
    key: "whatsapp",
    label: "VerbaMessenger",
    icon: (
      <VerbamessengerColouredIcon className="w-[15px] h-[15px] text-verba-green-success dark:text-verba-green-success-dk" />
    ),
    colorClass: "text-verba-messenger dark:text-verba-messenger-dk",
  },
  {
    key: "email",
    label: "VerbaMail",
    icon: <VerbamailIcon className="w-[15px] h-[15px]" />,
    colorClass: "text-verba-mail dark:text-verba-mail-dk",
  },
  {
    key: "call",
    label: "VerbaCall",
    icon: <VerbacallIcon className="w-[15px] h-[15px]" />,
    colorClass: "text-verba-lavender dark:text-verba-lavender-dk",
  },
  {
    key: "sms",
    label: "VerbaSMS",
    icon: <VerbasmsIcon className="w-[15px] h-[15px]" />,
    colorClass: "text-verba-sms dark:text-verba-sms-dk",
  },
  {
    key: "fb_messenger",
    label: "Facebook Messenger",
    icon: <FbMessengerIcon className="w-[15px] h-[15px]" />,
    colorClass: "text-verba-fb-messenger dark:text-verba-fb-messenger-dk",
  },
  {
    key: "instagram",
    label: "Instagram DMs",
    icon: <InstagramIcon className="w-[15px] h-[15px]" />,
    colorClass: "",
  },
];

export const getModalityLabel = (modality: string) => {
  switch (modality) {
    case "widget":
      return "VerbaChat";
    case "whatsapp":
      return "VerbaMessenger";
    case "email":
      return "VerbaMail";
    case "call":
      return "VerbaCall";
    case "sms":
      return "VerbaSMS";
    case "fb_messenger":
      return "Facebook Messenger";
    case "instagram":
      return "Instagram DMs";
    default:
      return modality;
  }
};

export const getModalityIcon = (modality: string) => {
  switch (modality) {
    case "widget":
    case "chat":
      return (
        <VerbachatIcon className="w-[12px] h-[12px] text-verba-blue dark:text-verba-blue-dk" />
      );
    case "whatsapp":
      return (
        <VerbamessengerIcon className="w-[12px] h-[12px] text-verba-green-success dark:text-verba-green-success-dk" />
      );
    case "email":
      return (
        <VerbamailIcon className="w-[12px] h-[12px] text-verba-mail dark:text-verba-mail-dk" />
      );
    case "call":
      return (
        <VerbacallIcon className="w-[12px] h-[12px] text-verba-lavender dark:text-verba-lavender-dk" />
      );
    case "sms":
      return (
        <VerbasmsIcon className="w-[12px] h-[12px] text-verba-sms dark:text-verba-sms-dk" />
      );
    case "fb_messenger":
      return (
        <FbMessengerIcon className="w-[12px] h-[12px] text-verba-fb-messenger dark:text-verba-fb-messenger-dk" />
      );
    case "instagram":
      return <InstagramIcon className="w-[12px] h-[12px]" />;
    case "mentions":
      return <MentionsIcon className="w-[12px] h-[12px]" />;
    default:
      return (
        <TicketIcon className="w-[12px] h-[12px] text-verba-indigo dark:text-verba-indigo-dk" />
      );
  }
};

export const FORM_FIELDS_CONFIG: FormFieldsConfig = {
  settings: {
    fields: [
      {
        field_key: "name",
        display_name: "Name",
        field_data_type: "string",
        input_type: "text",
        allows_multiple: false,
        option_source: null,
        is_enabled: true,
        is_mandatory: true,
        display_order: 0,
        has_dynamic_values: false,
        possible_values: null,
        parent_field_key: null,
        child_field_keys: null,
        placeholder: "Enter name",
      },
      {
        field_key: "email",
        display_name: "Email Address",
        field_data_type: "email",
        input_type: "email",
        allows_multiple: false,
        option_source: null,
        is_enabled: true,
        is_mandatory: false,
        display_order: 1,
        has_dynamic_values: false,
        possible_values: null,
        parent_field_key: null,
        child_field_keys: null,
        placeholder: "Enter email address",
      },
      {
        field_key: "phone",
        display_name: "Phone Number",
        field_data_type: "phone",
        input_type: "phone",
        allows_multiple: false,
        option_source: null,
        is_enabled: true,
        is_mandatory: false,
        display_order: 2,
        has_dynamic_values: false,
        possible_values: null,
        parent_field_key: null,
        child_field_keys: null,
        placeholder: "Enter phone number",
      },
      {
        field_key: "whatsapp_phone",
        display_name: "Whatsapp Number",
        field_data_type: "phone",
        input_type: "phone",
        allows_multiple: false,
        option_source: null,
        is_enabled: true,
        is_mandatory: false,
        display_order: 3,
        has_dynamic_values: false,
        possible_values: null,
        parent_field_key: null,
        child_field_keys: null,
        placeholder: "Enter whatsapp number",
      },

      {
        field_key: "demographics.destination_country",
        display_name: "Destination Country",
        field_data_type: "string",
        input_type: "searchable_dropdown_single",
        allows_multiple: false,
        option_source: "dynamic",
        is_enabled: true,
        is_mandatory: false,
        display_order: 4,
        has_dynamic_values: true,
        possible_values: null,
        parent_field_key: null,
        child_field_keys: ["demographics.destination_city"],
        placeholder: "Select destination country",
      },
      {
        field_key: "demographics.destination_city",
        display_name: "Destination City",
        field_data_type: "string",
        input_type: "searchable_dropdown_single",
        allows_multiple: false,
        option_source: "dynamic",
        is_enabled: true,
        is_mandatory: false,
        display_order: 5,
        has_dynamic_values: true,
        possible_values: null,
        parent_field_key: "demographics.destination_country",
        child_field_keys: ["demographics.university"],
        placeholder: "Select destination city",
      },
      {
        field_key: "demographics.university",
        display_name: "University",
        field_data_type: "string",
        input_type: "searchable_dropdown_single",
        allows_multiple: false,
        option_source: "dynamic",
        is_enabled: true,
        is_mandatory: false,
        display_order: 6,
        has_dynamic_values: true,
        possible_values: null,
        parent_field_key: "demographics.destination_city",
        child_field_keys: null,
        placeholder: "Select university",
      },

      {
        field_key: "move_in_date",
        display_name: "Move In Date",
        field_data_type: "date",
        input_type: "date_picker",
        allows_multiple: false,
        option_source: null,
        is_enabled: true,
        is_mandatory: false,
        display_order: 7,
        has_dynamic_values: false,
        possible_values: null,
        parent_field_key: null,
        child_field_keys: null,
        placeholder: "Select date",
      },
      {
        field_key: "room_type",
        display_name: "Room Type",
        field_data_type: "enum",
        input_type: "dropdown_multi",
        allows_multiple: true,
        option_source: "static",
        is_enabled: true,
        is_mandatory: false,
        display_order: 8,
        has_dynamic_values: false,
        possible_values: [
          "ensuite",
          "studio",
          "shared",
          "private",
          "apartment",
          "non_ensuite",
          "twin_ensuite",
          "twin_studio",
          "one_bed",
          "two_bed",
          "three_bed",
          "four_bed",
          "five_bed",
          "five_plus_bed",
          "shared_room",
          "private_room",
          "entire_place",
          "twodio",
          "threedio",
          "dorm",
          "standard_ensuite",
          "standard_ensuite_plus",
          "premium_ensuite",
          "premium_ensuite_dda",
          "deluxe_ensuite",
          "deluxe_ensuite_dda",
          "bronze_studio",
          "silver_studio",
          "gold_studio",
          "platinum_studio",
          "diamond_studio",
          "deluxe_non_ensuite",
          "premium_non_ensuite",
          "twin_non_ensuite",
          "classic_one_bed_apartment",
          "deluxe_one_bed_apartment",
          "the_grand_one_bed_apartment",
          "deluxe_2_bed_apartment",
          "the_grand_2_bed_apartment",
          "lite",
          "classic",
          "premium",
          "deluxe",
          "accessible",
          "classic_courtyard",
          "classic_plus",
          "premium_plus",
          "ultimate_vita",
          "lite_plus",
          "twin_room",
          "cluster",
          "penthouse",
          "enhanced",
          "elite",
          "duo",
          "essential",
          "essential_extra",
        ],
        parent_field_key: null,
        child_field_keys: null,
        placeholder: "Select room types",
      },

      {
        field_key: "tenant_type",
        display_name: "Tenant Type",
        field_data_type: "string",
        input_type: "dropdown_single",
        allows_multiple: false,
        option_source: null,
        is_enabled: true,
        is_mandatory: false,
        display_order: 9,
        has_dynamic_values: false,
        possible_values: ["Student", "Working Professional"],
        parent_field_key: null,
        child_field_keys: null,
        placeholder: "Select tenant type",
      },
      {
        field_key: "level_of_study",
        display_name: "Level Of Study",
        field_data_type: "enum",
        input_type: "dropdown_single",
        allows_multiple: false,
        option_source: "static",
        is_enabled: true,
        is_mandatory: false,
        display_order: 10,
        has_dynamic_values: false,
        possible_values: [
          "Undergraduate",
          "Postgraduate",
          "Non Student",
          "Doctor of Philosophy",
        ],
        parent_field_key: null,
        child_field_keys: null,
        placeholder: "Select level of study",
      },

      {
        field_key: "budget",
        display_name: "Budget",
        field_data_type: "object",
        input_type: "budget",
        allows_multiple: false,
        option_source: null,
        is_enabled: true,
        is_mandatory: false,
        display_order: 11,
        has_dynamic_values: false,
        possible_values: null,
        parent_field_key: null,
        child_field_keys: null,
        placeholder: null,
      },
      {
        field_key: "stay_duration",
        display_name: "Stay Duration",
        field_data_type: "number",
        input_type: "text",
        allows_multiple: false,
        option_source: null,
        is_enabled: true,
        is_mandatory: false,
        display_order: 12,
        has_dynamic_values: false,
        possible_values: null,
        parent_field_key: null,
        child_field_keys: null,
        placeholder: "Stay duration",
      },
      {
        field_key: "tags",
        display_name: "Tags",
        field_data_type: "enum",
        input_type: "tag_dropdown",
        allows_multiple: true,
        option_source: "both",
        is_enabled: true,
        is_mandatory: false,
        display_order: 13,
        has_dynamic_values: false,
        possible_values: [
          "general-enquiry",
          "finance-query",
          "complaint-query",
          "gdpr-query",
          "hr-query",
          "construction-query",
          "esg-query",
          "leasing-enquiry",
          "human-support-requested",
          "booking-request",
          "25/26-intake",
        ],
        parent_field_key: null,
        child_field_keys: null,
        placeholder: "Select tags",
      },
    ],
  },
};
